import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { createOrder } from '@/lib/api';
import { ShoppingBag, Minus, Plus, Trash2, CreditCard, ArrowLeft, CheckCircle } from 'lucide-react';

export default function Checkout() {
  const { user, isPro } = useAuth();
  const { cart, updateItem, removeItem, refreshCart } = useCart();
  const navigate = useNavigate();
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { refreshCart(); }, []);

  const handleOrder = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await createOrder({ delivery_notes: notes });
      setOrderResult(res.data);
      await refreshCart();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la commande');
    }
    setLoading(false);
  };

  if (orderResult) {
    return (
      <div data-testid="order-success" className="min-h-[70vh] flex items-center justify-center py-12 px-4">
        <div className="max-w-md text-center">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-[#111] mb-2">Commande confirmée !</h1>
          <p className="text-sm text-black/60 mb-4">N° {orderResult.order_number}</p>
          <div className="border border-black/10 p-4 mb-6 text-left">
            <div className="flex justify-between text-sm mb-2"><span>Total</span><strong>{orderResult.total.toFixed(2)} EUR</strong></div>
            <div className="flex justify-between text-sm mb-2"><span>Acompte commande (10%)</span><strong>{orderResult.deposit.toFixed(2)} EUR</strong></div>
            <div className="flex justify-between text-sm text-black/50"><span>Solde selon conditions</span><span>{(orderResult.total - orderResult.deposit).toFixed(2)} EUR</span></div>
          </div>
          <div className="flex gap-3 justify-center">
            <Link to="/orders" className="px-4 py-2 text-sm font-semibold text-[#00A5FF] border border-[#00A5FF] rounded-sm hover:bg-[#00A5FF]/5">Mes commandes</Link>
            <Link to="/catalog" className="px-4 py-2 text-sm font-semibold bg-[#00A5FF] text-white rounded-sm hover:bg-[#0090e0]">Continuer</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen">
      <div className="border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/catalog" className="inline-flex items-center gap-1 text-sm text-[#00A5FF] hover:underline mb-3"><ArrowLeft size={14} /> Continuer les achats</Link>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tight">Finaliser la commande</h1>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {cart.items.length === 0 ? (
          <div className="text-center py-20"><ShoppingBag size={48} className="mx-auto text-black/10 mb-4" /><p className="text-black/50">Panier vide</p></div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Items */}
            <div className="lg:col-span-2 space-y-3">
              {cart.items.map((item) => (
                <div key={item.product_id} className="border border-black/10 p-4 flex gap-4">
                  <div className="w-16 h-16 bg-[#F8F9FA] border border-black/5 shrink-0 flex items-center justify-center"><ShoppingBag size={20} className="text-black/10" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#111] truncate">{item.name}</p>
                    <p className="text-xs text-black/40">SKU: {item.sku} | Délai: {item.delivery_delay}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-black/15">
                        <button onClick={() => updateItem(item.product_id, Math.max(1, item.quantity - 1))} className="p-1.5 hover:bg-[#F8F9FA]"><Minus size={12} /></button>
                        <span className="px-3 text-sm font-medium">{item.quantity}</span>
                        <button onClick={() => updateItem(item.product_id, item.quantity + 1)} className="p-1.5 hover:bg-[#F8F9FA]"><Plus size={12} /></button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold">{item.line_total.toFixed(2)} EUR</span>
                        <button onClick={() => removeItem(item.product_id)} className="text-black/30 hover:text-[#E8231B]"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Notes de livraison</label>
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Instructions spéciales..." className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] outline-none resize-none" />
              </div>
            </div>
            {/* Summary */}
            <div className="border border-black/10 p-6 h-fit sticky top-24">
              <h3 className="text-sm font-bold uppercase tracking-wider text-black/50 mb-4">Résumé</h3>
              <div className="space-y-2 mb-4 pb-4 border-b border-black/10">
                <div className="flex justify-between text-sm"><span>Sous-total</span><span>{cart.total.toFixed(2)} EUR</span></div>
                <div className="flex justify-between text-sm"><span>TVA</span><span>Incluse</span></div>
              </div>
              <div className="flex justify-between text-lg font-extrabold mb-4"><span>Total</span><span>{cart.total.toFixed(2)} EUR</span></div>
              <div className="space-y-1 mb-4 p-3 bg-[#F8F9FA] text-xs text-black/60">
                <div className="flex justify-between"><span>Acompte commande (10%)</span><strong>{(cart.total * 0.1).toFixed(2)} EUR</strong></div>
                <div className="flex justify-between"><span>A la livraison (10%)</span><strong>{(cart.total * 0.1).toFixed(2)} EUR</strong></div>
                <div className="flex justify-between"><span>Solde à 30 jours (80%)</span><span>{(cart.total * 0.8).toFixed(2)} EUR</span></div>
              </div>
              {error && <p className="text-sm text-[#E8231B] mb-3">{error}</p>}
              <button
                onClick={handleOrder} disabled={loading}
                data-testid="place-order-button"
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#00A5FF] text-white font-semibold text-sm rounded-sm hover:bg-[#0090e0] disabled:opacity-50 transition-colors duration-200"
              >
                <CreditCard size={16} />
                {loading ? 'Commande en cours...' : 'Confirmer la commande'}
              </button>
              <p className="text-xs text-black/40 text-center mt-2">Paiement Stripe en V2</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
